import React, { useState } from 'react';
import { Box, Button, Stack } from '@mui/material';

import { BasePage } from '../../components/base-page';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  GetExpensesForAccountDocument,
  GetExpensesForAccountQuery,
  CreateExpenseDocument,
  CreateExpenseMutation,
  CreateExpenseMutationVariables,
  CreateMoneyTransactionDocument,
  CreateMoneyTransactionMutation,
  CreateMoneyTransactionMutationVariables,
  CreateExpensePaymentDocument,
  CreateExpensePaymentMutation,
  CreateExpensePaymentMutationVariables,
} from '../../generated/graphql';
import { useNavigate, useSearchParams } from 'react-router';
import { appPaths } from '../../app-paths';
import { useAlert } from '@dwelvio/react-alert';
import { useApp } from '../../components/app-context';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import CircleIcon from '@mui/icons-material/Circle';
import { NewExpenseDialog } from '../../components/new-expense-dialog';
import { NewCashExpenseDialog } from '../../components/new-cash-expense-dialog';
import { AddIcon } from '../../components/icons';

const FilterGroupsArray = ['all', 'unpaid', 'overdue'] as const;
type FilterGroupType = (typeof FilterGroupsArray)[number];

export function ExpenseListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { show } = useAlert();
  const app = useApp();
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [showCashDialog, setShowCashDialog] = useState(false);

  const { data, loading, error, refetch } = useQuery<GetExpensesForAccountQuery>(GetExpensesForAccountDocument);

  React.useEffect(() => {
    if (error) {
      show.error('Nepodarilo sa načítať výdavky.', error.message);
    }
  }, [error, show]);

  const [createExpense, { loading: createLoading }] = useMutation<
    CreateExpenseMutation,
    CreateExpenseMutationVariables
  >(CreateExpenseDocument, {
    onError: (e: Error) => show.error('Nepodarilo sa vytvoriť výdavok.', e.message),
  });

  const [createTransaction, { loading: transactionLoading }] = useMutation<
    CreateMoneyTransactionMutation,
    CreateMoneyTransactionMutationVariables
  >(CreateMoneyTransactionDocument, {
    onError: (e: Error) => show.error('Nepodarilo sa vytvoriť transakciu.', e.message),
  });

  const [createExpensePayment, { loading: paymentLoading }] = useMutation<
    CreateExpensePaymentMutation,
    CreateExpensePaymentMutationVariables
  >(CreateExpensePaymentDocument, {
    onError: (e: Error) => show.error('Nepodarilo sa priradiť platbu k výdavku.', e.message),
  });

  const filterGroup: FilterGroupType = (searchParams.get('fg') as FilterGroupType) ?? 'all';
  if (filterGroup && FilterGroupsArray.includes(filterGroup as FilterGroupType) === false) {
    searchParams.set('fg', 'all');
    setSearchParams(searchParams);
  }

  const handleFilterGroupChange = (g: FilterGroupType) => {
    searchParams.set('fg', g);
    setSearchParams(searchParams);
  };

  const expenses = data?.getExpensesForAccount ?? [];

  const filteredExpenses = React.useMemo(
    () =>
      expenses.filter((expense) => {
        if (expense.deletedAt) return false;
        if (expense.isCost) return filterGroup === 'all';

        const isUnpaid = !expense.paidAt;
        const isOverdue = isUnpaid && new Date(expense.dueDate).getTime() < Date.now();

        if (!isOverdue && filterGroup === 'overdue') return false;
        if (!isUnpaid && filterGroup === 'unpaid') return false;

        return true;
      }),
    [expenses, filterGroup],
  );

  const columns = React.useMemo(
    (): GridColDef[] => [
      {
        field: 'paidAt',
        headerName: '',
        width: 50,
        disableColumnMenu: true,
        sortable: false,
        renderCell: (params) => {
          if (params.row.isCost || params.value) return null;
          const now = Date.now();
          const dueDate = new Date(params.row.dueDate).getTime();
          return <CircleIcon sx={{ color: dueDate < now ? 'red' : 'orange', fontSize: 'small' }} />;
        },
      },
      {
        field: 'date',
        headerName: 'Dátum',
        width: 130,
        valueFormatter: (value: string) => app.formatDate(value),
        getSortComparator: (sortDirection) => (v1, v2, p1, p2) => {
          const t1 = new Date(v1 as string).getTime();
          const t2 = new Date(v2 as string).getTime();
          if (t1 !== t2) return sortDirection === 'desc' ? t2 - t1 : t1 - t2;
          const c1 = new Date((p1.api.getRow(p1.id) as { createdAt: string }).createdAt).getTime();
          const c2 = new Date((p2.api.getRow(p2.id) as { createdAt: string }).createdAt).getTime();
          return sortDirection === 'desc' ? c2 - c1 : c1 - c2;
        },
      },
      {
        field: 'title',
        headerName: 'Názov',
        flex: 1,
        minWidth: 200,
      },
      {
        field: 'amount',
        headerName: 'Suma',
        width: 130,
        valueFormatter: (value: number) => app.formatNumber(value, 2) + ' €',
      },
      {
        field: 'unitAssignments',
        headerName: 'Nepriradená suma',
        width: 160,
        sortable: false,
        valueGetter: (_value: unknown, row: { amount: number; unitAssignments?: Array<{ amount: number }> }) => {
          const assigned = (row.unitAssignments ?? []).reduce((sum, a) => sum + a.amount, 0);
          return Math.round((row.amount - assigned) * 100) / 100;
        },
        renderCell: (params) => {
          const unassigned = params.value as number;
          const color = unassigned > 0.001 ? 'red' : 'inherit';
          const text = unassigned > 0.001 ? app.formatNumber(unassigned, 2) + ' €' : '—';
          return <span style={{ color }}>{text}</span>;
        },
      },
      {
        field: 'dueDate',
        headerName: 'Splatnosť',
        width: 130,
        valueFormatter: (value: string) => app.formatDate(value),
      },
    ],
    [app],
  );

  /** Create a cash expense: expense + DEBIT cash transaction + expense payment */
  const handleCashExpenseSubmit = async (input: {
    title: string;
    amount: number;
    taxDeductible: number;
    date: string;
  }) => {
    // 1. Create the expense
    const expenseResult = await createExpense({
      variables: {
        input: {
          title: input.title,
          amount: input.amount,
          taxDeductible: input.taxDeductible,
          date: input.date,
          dueDate: input.date, // same as date for cash expenses
          isCost: false,
        },
      },
    });
    const createdExpense = expenseResult.data?.createExpense?.expense;
    if (!createdExpense) return;

    // 2. Create a DEBIT cash transaction
    const txResult = await createTransaction({
      variables: {
        input: {
          transactionDate: input.date,
          type: 'DEBIT',
          value: input.amount,
          cash: true,
          bankAccountNo: 'cash',
        },
      },
    });
    const createdTransaction = txResult.data?.createMoneyTransaction?.moneyTransaction;
    if (!createdTransaction) {
      show.error('Výdavok bol vytvorený, ale nepodarilo sa vytvoriť platobnú transakciu.');
      setShowCashDialog(false);
      refetch();
      return;
    }

    // 3. Link transaction to expense as payment
    const paymentResult = await createExpensePayment({
      variables: {
        input: {
          expenseId: createdExpense.id,
          transactionId: createdTransaction.id,
          amount: input.amount,
        },
      },
    });

    if (paymentResult.data?.createExpensePayment?.errors?.length) {
      show.error('Výdavok a transakcia boli vytvorené, ale nepodarilo sa ich prepojiť. Prosím, priraďte manuálne.');
    } else {
      show.info('Hotovostný výdavok bol úspešne vytvorený a zaplatený.');
    }
    setShowCashDialog(false);
    refetch();
  };

  /** Create an invoice expense (no payment record) */
  const handleInvoiceExpenseSubmit = async (input: {
    title: string;
    amount: number;
    taxDeductible: number;
    date: string;
    dueDate?: string;
    refNo?: string;
    isCost?: boolean;
  }) => {
    const result = await createExpense({ variables: { input } });
    const createdId = result.data?.createExpense?.expense?.id;
    if (createdId) {
      show.info('Výdavok bol úspešne vytvorený.');
      setShowInvoiceDialog(false);
      refetch();
    }
  };

  const anyLoading = createLoading || transactionLoading || paymentLoading;

  return (
    <BasePage title="Výdavky" loading={loading} errorMsg={error ? 'Nepodarilo sa načítať výdavky.' : undefined}>
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" spacing={2} sx={{ marginBottom: 2 }} useFlexGap flexWrap="wrap" alignItems="center">
          <Button
            variant={filterGroup !== 'all' ? 'outlined' : 'contained'}
            onClick={() => handleFilterGroupChange('all')}
          >
            Všetky
          </Button>
          <Button
            variant={filterGroup !== 'unpaid' ? 'outlined' : 'contained'}
            onClick={() => handleFilterGroupChange('unpaid')}
          >
            Nezaplatené
          </Button>
          <Button
            variant={filterGroup !== 'overdue' ? 'outlined' : 'contained'}
            onClick={() => handleFilterGroupChange('overdue')}
          >
            Po splatnosti
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setShowCashDialog(true)}>
            Hotovostný výdavok
          </Button>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setShowInvoiceDialog(true)}>
            Výdavok (faktúra)
          </Button>
        </Stack>

        <Box sx={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={filteredExpenses}
            columns={columns}
            initialState={{ sorting: { sortModel: [{ field: 'date', sort: 'desc' }] } }}
            onRowClick={(params) => navigate(appPaths.expense.navigate({ expenseId: params.row.id }))}
            sx={{ cursor: 'pointer' }}
          />
        </Box>
      </Box>

      <NewCashExpenseDialog
        open={showCashDialog}
        onClose={() => setShowCashDialog(false)}
        loading={anyLoading}
        onSubmit={handleCashExpenseSubmit}
      />

      <NewExpenseDialog
        open={showInvoiceDialog}
        onClose={() => setShowInvoiceDialog(false)}
        loading={anyLoading}
        onSubmit={handleInvoiceExpenseSubmit}
      />
    </BasePage>
  );
}
